import { from, interval, Observable, of } from 'rxjs';
import { filter, first, retry, switchMap, takeUntil } from 'rxjs/operators';
import { CapabilityData } from '../transaction-signing.model';
import { AsyncPollingOptions, ProgressStatus, TransactionSigningOptions } from './transaction-signing-helper.model';

export class TransactionSigningHelperService {
  private readonly defaultPollInterval = 1000;
  private readonly defaultMaxRetries = 3;
  private readonly defaultStatusMap = {
    success: [ProgressStatus.ACCEPTED, ProgressStatus.PROCESSED, ProgressStatus.PENDING_APPROVAL],
    rejected: [ProgressStatus.REJECTED],
    declined: [ProgressStatus.SYSTEM_DECLINED, ProgressStatus.USER_DECLINED],
    pending: [ProgressStatus.CONFIRMATION_PENDING, ProgressStatus.SENDING],
  };

  /**
   * Determines if the response was returned from the Transaction Signing flow.
   * @param response the data returned after a possible transaction signing request.
   * @returns boolean
   */
  isTransactionSigningResponse(response: CapabilityData) {
    return response && !!response['confirmation-type'];
  }

  /**
   * Polls a fetch function. Designed to run after transaction signing has occurred.
   * @param data  this is passed to the fetchFn as param.
   * @param fetchFn a function that triggers an http call.
   * @param pollOptions options to configure the polling such as pollInterval, maxRetries etc.
   * @param pendingStatuses array of statuses which determines if the polling should continue.
   * @returns an Observable that sequencially emits a value based on configured time interval.
   */
  setupPolling(
    data: any,
    fetchFn: (param: any) => Observable<any>,
    pollOptions: AsyncPollingOptions,
    pendingStatuses: string[],
  ) {
    const pollUntil = pollOptions.pollUntilFn || this.defaultPollUntilFn.bind(this, pendingStatuses);
    return interval(pollOptions.pollInterval || this.defaultPollInterval).pipe(
      takeUntil(pollOptions.complete$),
      switchMap(() => from(fetchFn(data))),
      retry(pollOptions.maxRetries || this.defaultMaxRetries),
      filter((res) => pollUntil(res)),
      first(),
    );
  }

  /**
   * Handles scenarios after signing a transaction and triggers appropriate callbacks.
   * @param options Configuration for determining different flows of the transaction.
   */
  getTransactionInfo(options: TransactionSigningOptions) {
    if (!!options.asyncPollingOptions) {
      this.handleAsyncPollingFlow(options);
    } else {
      this.handleGenericFlow(options);
    }
  }

  private defaultPollUntilFn(pendingStatuses: Array<string>, status: string) {
    return !!status && !pendingStatuses.includes(status);
  }

  private handleAsyncPollingFlow(options: TransactionSigningOptions) {
    const { asyncPollingOptions } = options;
    const { pending } = options.statusMap || this.defaultStatusMap;

    if (options.fetchFn) {
      this.setupPolling(options.data, options.fetchFn, <AsyncPollingOptions>asyncPollingOptions, pending).subscribe(
        (status) => {
          this.triggerTransactionCallback({ status }, options);
        },
        (error) => {
          options.onError(error);
        },
      );
    } else {
      console.warn('`fetchFn` is mandatory when polling options are provided');
    }
  }

  private handleGenericFlow(options: TransactionSigningOptions) {
    const item$ = options.fetchFn ? options.fetchFn(options.data) : of(options.data);

    item$.pipe(first()).subscribe(
      (response) => {
        this.triggerTransactionCallback(response, options);
      },
      (error) => {
        options.onError(error);
      },
    );
  }

  private triggerTransactionCallback(response: any, options: TransactionSigningOptions) {
    const { status } = response || {};
    const { success, declined, rejected } = options.statusMap || this.defaultStatusMap;

    if (success.includes(status)) {
      options.onSuccess(response);
    } else if (declined.includes(status)) {
      options.onDeclined(response);
    } else if (rejected.includes(status)) {
      options.onRejected(response);
    } else {
      options.onError(response);
    }
  }
}
